import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink } from "../../../../../common/components/ExternalLink";
import { SettingRow } from "./SettingRow";
import {
  AlertCard,
  Button as AquiloButton,
  Dropdown,
  Modal,
  ToolTip,
  useSnackBar,
} from "@truevault/aquilo";
import { SendMethod } from "../../../../../common/service/server/Types";
import { OrganizationPostmarkMailboxDto } from "../../../../../common/service/server/dto/OrganizationDto";
import { usePrimaryOrganizationId } from "../../../../hooks/useOrganization";
import { useEditModeReadOnlyGuard } from "../../../../edit-mode/useIsEditModeReadOnly";

type Props = {
  mailbox: string;
  mailboxIsConnected: boolean;
  mailboxOauthUrl: string;
  sendTestMessage: () => Promise<void>;
  testMessageRunning: boolean;
  deleteMailbox: () => void;
  vertical?: boolean;
  label?: string;
  sendMethod?: SendMethod;
  onSendMethodChange: (method: SendMethod) => Promise<void> | void;
  postmarkMailbox?: OrganizationPostmarkMailboxDto | null;
};

const helpText =
  "Connect your privacy email account so your team can process consumer privacy requests from Polaris.";

export const EmailAccountSettingRow: React.FC<Props> = ({
  mailbox,
  mailboxIsConnected,
  mailboxOauthUrl,
  sendTestMessage,
  testMessageRunning,
  deleteMailbox: _deleteMailbox,
  sendMethod,
  onSendMethodChange,
  postmarkMailbox,
  vertical = false,
  label = "Email Account",
}) => {
  const orgId = usePrimaryOrganizationId();
  const domainSettingsUrl = useMemo(() => `/organization/${orgId}/settings/domain`, [orgId]);
  const providerOptions = useMemo(
    () => [
      { value: "NYLAS" as SendMethod, label: "Google Workspace / MS Outlook" },
      { value: "POSTMARK" as SendMethod, label: "Email Forwarding" },
    ],
    [],
  );
  const placeholder = "Select email delivery method";

  const [selectedMethod, setSelectedMethod] = useState<SendMethod | "">(sendMethod ?? "");
  const [showSwitchBackConfirmModal, setShowSwitchBackConfirmModal] = useState(false);
  const [testEmailSent, setTestEmailSent] = useState(false);
  const [methodChangeRunning, setMethodChangeRunning] = useState(false);
  const snackbar = useSnackBar();
  const guardReadOnlyMode = useEditModeReadOnlyGuard();

  useEffect(() => {
    setSelectedMethod(sendMethod ?? "");
    setTestEmailSent(false);
  }, [sendMethod]);

  const sharedChildOrganizations = postmarkMailbox?.sharedWithChildOrganizations ?? [];
  const postmarkEmailLocked = selectedMethod === "POSTMARK" && sharedChildOrganizations.length > 0;
  const sharedChildOrganizationNames = sharedChildOrganizations
    .map((childOrg) => childOrg.organizationName)
    .join(", ");
  const postmarkEmailLockTooltip = postmarkEmailLocked
    ? `This Postmark email is used by child organizations: ${sharedChildOrganizationNames}. Remove sharing before changing the email delivery method.`
    : null;

  const applySendMethodChange = async (val: SendMethod) => {
    setMethodChangeRunning(true);
    try {
      await onSendMethodChange(val);
      setSelectedMethod(val);
    } catch {
      setSelectedMethod(sendMethod ?? "");
    } finally {
      setMethodChangeRunning(false);
    }
  };

  const handleSelect = (val: SendMethod | "") => {
    if (guardReadOnlyMode() || methodChangeRunning) {
      return;
    }

    if (postmarkEmailLocked && val === "NYLAS") {
      return;
    }

    if (val === "NYLAS" && selectedMethod === "POSTMARK") {
      setShowSwitchBackConfirmModal(true);
      return;
    }

    if (val === "NYLAS" || val === "POSTMARK") {
      void applySendMethodChange(val);
    }
  };

  const handleConfirmSwitchBackToNylas = () => {
    if (guardReadOnlyMode()) {
      return;
    }

    void applySendMethodChange("NYLAS");
    setShowSwitchBackConfirmModal(false);
  };

  const handleConnect = () => {
    if (guardReadOnlyMode()) {
      return;
    }

    if (!mailboxOauthUrl) return;
    window.open(mailboxOauthUrl);
  };

  const handleDisconnect = () => {
    if (guardReadOnlyMode()) {
      return;
    }

    _deleteMailbox();
  };

  const handleSendTestEmail = async () => {
    if (guardReadOnlyMode()) {
      return;
    }

    try {
      await sendTestMessage();
      setTestEmailSent(true);
      snackbar("Test email sent.", { variant: "success" });
    } catch {
      // Errors are surfaced via the shared request handler.
    }
  };

  const selectedProviderLabel =
    providerOptions.find((option) => option.value === selectedMethod)?.label ?? placeholder;

  const isNylasSelected = selectedMethod === "NYLAS";
  const isForwardingSelected = selectedMethod === "POSTMARK";
  const forwardingStatus = postmarkMailbox?.forwardingStatus;
  const forwardingIsConfirmed = forwardingStatus === "CONFIRMED";
  const postmarkDomainVerified = postmarkMailbox?.status === "VERIFIED";
  const canSendTestEmail = useMemo(() => {
    if (!selectedMethod) {
      return false;
    }
    if (selectedMethod === "NYLAS") {
      return Boolean(mailboxIsConnected && mailbox);
    }
    if (selectedMethod === "POSTMARK") {
      return Boolean(postmarkMailbox?.mailbox && postmarkDomainVerified);
    }
    return false;
  }, [
    selectedMethod,
    mailboxIsConnected,
    mailbox,
    postmarkMailbox?.mailbox,
    postmarkDomainVerified,
  ]);
  const forwardingEmail = useMemo(() => {
    if (postmarkMailbox?.mailbox) {
      return postmarkMailbox.mailbox;
    }
    if (mailbox) {
      return mailbox;
    }
    return "Your privacy email";
  }, [postmarkMailbox?.mailbox, mailbox]);

  return (
    <SettingRow title={label} helpText={helpText} vertical={vertical}>
      <div className="flex-col">
        <div className="tw-boundary">
          <div className="flex flex-wrap items-center gap-sm">
            <div className="flex flex-wrap items-center gap-sm">
              <div className="w-[320px] mr-0">
                <ToolTip text={postmarkEmailLockTooltip} width="260px">
                  <span className="inline-block w-full">
                    <Dropdown
                      label={selectedProviderLabel}
                      className="w-full !mr-0"
                      buttonClassName={`w-full !justify-between ${
                        postmarkEmailLocked
                          ? "pointer-events-none !cursor-not-allowed !bg-neutral-50 !text-neutral-400"
                          : ""
                      }`}
                      menuClassName="w-full"
                      sections={[
                        {
                          id: "email-delivery-methods",
                          items: providerOptions.map((opt) => ({
                            id: opt.value,
                            label: opt.label,
                            selected: selectedMethod === opt.value,
                            disabled:
                              methodChangeRunning || (postmarkEmailLocked && opt.value === "NYLAS"),
                            onSelect: () => handleSelect(opt.value),
                          })),
                        },
                      ]}
                    />
                  </span>
                </ToolTip>
              </div>
              <AquiloButton
                color={testEmailSent ? "tertiary" : "primary"}
                size="s"
                customSizeClassname="h-[40px] py-[9px] text-sm leading-5"
                onClick={() => {
                  void handleSendTestEmail();
                }}
                disabled={testMessageRunning || !canSendTestEmail}
              >
                {testMessageRunning
                  ? "Sending..."
                  : testEmailSent
                  ? "Resend Test Email"
                  : "Send test email"}
              </AquiloButton>
            </div>

            {isNylasSelected && (
              <div className="flex flex-wrap items-center gap-sm">
                {mailboxIsConnected && mailbox ? (
                  <>
                    <AquiloButton color="tertiary" onClick={handleDisconnect} size="s">
                      Disconnect Email
                    </AquiloButton>
                    <AlertCard
                      variant="success"
                      size="compact"
                      title={`${mailbox} is connected`}
                      containerClasses="mt-0 mb-0"
                    />
                  </>
                ) : (
                  <AquiloButton
                    color="primary"
                    onClick={handleConnect}
                    size="s"
                    customSizeClassname="h-[40px] py-[9px] text-sm leading-5"
                  >
                    Connect Email
                  </AquiloButton>
                )}
              </div>
            )}
          </div>
        </div>
        {isNylasSelected && (
          <div className="text-body-2">
            <div>
              Using Google Workspace?{" "}
              <ExternalLink
                className="regular-link text-medium"
                href="https://knowledge.truevault.com/article/282-add-truevault-polaris-as-a-trusted-app"
                target="_blank"
              >
                Follow the authorization instructions
              </ExternalLink>
            </div>
          </div>
        )}

        {isForwardingSelected && (
          <div className="tw-boundary mt-sm space-y-sm pt-sm pb-sm">
            {forwardingIsConfirmed && (
              <AlertCard
                variant="success"
                title={`${forwardingEmail} is forwarding`}
                description={
                  <div className="text-body-2">
                    <div className="mb-xs">
                      To check your forwarding configuration, go to the Domains page in Account
                      Settings and follow the instructions.
                    </div>
                    <a className="regular-link text-medium" href={domainSettingsUrl}>
                      Go to Email Forwarding Config
                    </a>
                  </div>
                }
                containerClasses="mt-0 mb-0"
              />
            )}
            {!forwardingIsConfirmed && (
              <AlertCard
                variant="info"
                title="Email forwarding setup"
                description={
                  <div className="text-body-2">
                    <div className="mb-xs">
                      To check your forwarding configuration, go to the Domains page in Account
                      Settings and follow the instructions.
                    </div>
                    <a className="regular-link text-medium" href={domainSettingsUrl}>
                      Go to Email Forwarding Config
                    </a>
                  </div>
                }
                containerClasses="mt-0 mb-0"
              />
            )}
          </div>
        )}
      </div>
      {showSwitchBackConfirmModal && (
        <Modal
          closeButton={true}
          showOverlay={true}
          closeOnOverlayClick={true}
          title="Switch Back to Connected Email?"
          body="You are currently using Email Forwarding (Postmark). Switching back to Google Workspace / MS Outlook requires an active connected mailbox in this org. Are you sure you want to continue?"
          firstButton="Cancel"
          secondButton="Yes, Switch"
          firstButtonColor="tertiary"
          secondButtonColor="primary"
          alignText="left"
          alignButton="right"
          onFirstButtonClick={() => setShowSwitchBackConfirmModal(false)}
          onClose={() => setShowSwitchBackConfirmModal(false)}
          onSecondButtonClick={handleConfirmSwitchBackToNylas}
        />
      )}
    </SettingRow>
  );
};
